import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { Client } from '@/lib/Client';
import type { Payment, PaymentAttributes, PaymentBy } from '@/lib/Payment';
import { t } from '@/lib/i18n';
import { Badge, Box, Button, CloseButton, Collapsible, Drawer, Grid, HStack, Input, Portal, Skeleton, Table, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { mutate as globalMutate } from 'swr';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

function formatDate(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('uk-UA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function nowLocal(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const selectStyle = {
    width: '100%', padding: '4px 8px', borderRadius: '4px',
    border: '1px solid #ccc', fontSize: '14px', background: 'transparent',
};

function PaymentFormDrawer({ title, clientOptions, open, onClose, onSave, getAccessToken }: {
    title: string; clientOptions: Client[]; open: boolean; onClose: () => void;
    onSave: (attrs: PaymentAttributes) => Promise<string | undefined>;
    getAccessToken: () => Promise<string>;
}) {
    const [clientId, setClientId] = useState(clientOptions[0]?.id ?? '');
    const [amount, setAmount] = useState('');
    const [dateTime, setDateTime] = useState(nowLocal());
    const [comment, setComment] = useState('');
    const [paymentBy, setPaymentBy] = useState<PaymentBy>('cash');
    const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        const amt = parseInt(amount, 10);
        if (!clientId) { setError(t('payments.client_required')); return; }
        if (isNaN(amt) || amt < 0) { setError(t('payments.amount_invalid')); return; }
        setSubmitting(true); setError('');
        try {
            const paymentId = await onSave({ client_id: clientId, amount: amt, date_time: new Date(dateTime).toISOString(), comment: comment.trim() || undefined, payment_by: paymentBy, deleted: false });
            // Upload attachment if a file was selected
            if (paymentId && attachmentFile) {
                const token = await getAccessToken();
                const res = await apiRequest<{ upload_url: string }>('POST', `/payments/${paymentId}/attachment/upload`, token);
                await fetch(res.upload_url, { method: 'PUT', body: attachmentFile });
            }
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : t('error.unknown')); }
        finally { setSubmitting(false); }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal><Drawer.Backdrop /><Drawer.Positioner><Drawer.Content>
                <Drawer.Header><Drawer.Title>{title}</Drawer.Title></Drawer.Header>
                <Drawer.Body><VStack gap={3} align="stretch">
                    {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                    <Box><Text fontSize="sm" mb="1">{t('label.client')} *</Text>
                        <select value={clientId} onChange={e => setClientId(e.target.value)} style={selectStyle}>
                            {clientOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.amount')} (₴) *</Text><Input size="sm" type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.date')} *</Text><Input size="sm" type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.payment_by')} *</Text>
                        <select value={paymentBy} onChange={e => setPaymentBy(e.target.value as PaymentBy)} style={selectStyle}>
                            <option value="cash">{t('payment_by.cash')}</option><option value="card">{t('payment_by.card')}</option>
                        </select>
                    </Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.comment')}</Text><Input size="sm" value={comment} onChange={e => setComment(e.target.value)} /></Box>
                    <Box>
                        <Text fontSize="sm" mb="1">{t('label.receipt')}</Text>
                        <input type="file" accept="image/*" hidden id="payment-attachment" onChange={e => setAttachmentFile(e.target.files?.[0] ?? null)} />
                        <Button size="xs" variant="outline" onClick={() => document.getElementById('payment-attachment')?.click()}>
                            {attachmentFile ? attachmentFile.name : t('action.choose_file')}
                        </Button>
                        {attachmentFile && (
                            <Button size="xs" variant="ghost" ml={2} onClick={() => setAttachmentFile(null)}>{t('action.clear')}</Button>
                        )}
                    </Box>
                </VStack></Drawer.Body>
                <Drawer.Footer><HStack><Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button><Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button></HStack></Drawer.Footer>
                <Drawer.CloseTrigger asChild><CloseButton size="xs" variant="solid" /></Drawer.CloseTrigger>
            </Drawer.Content></Drawer.Positioner></Portal>
        </Drawer.Root>
    );
}

export function Payments() {
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchText, setSearchText] = useState('');
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    // --- Filter param helpers ---
    const getFilter = useCallback((key: string): string => {
        return searchParams.get(key) ?? '';
    }, [searchParams]);

    const setFilter = useCallback((key: string, value: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value);
            else next.delete(key);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const clearFilters = useCallback(() => {
        setSearchParams({}, { replace: true });
    }, [setSearchParams]);

    const activeFilterCount = useMemo(() => {
        const filterKeys = ['date_from', 'date_to', 'client_id', 'amount_min', 'amount_max', 'comment'];
        return filterKeys.filter(k => searchParams.has(k)).length;
    }, [searchParams]);

    // Build API URL from search params
    const apiKey = useMemo(() => {
        const params = searchParams.toString();
        return params ? `/payments?${params}` : '/payments';
    }, [searchParams]);

    // --- Data fetching ---
    const { data: paymentsData, error, isLoading, isAuthenticated, mutate: mutatePayments } =
        useAuthorizedRequest<{ payments: Payment[] }>(apiKey, jsonFetcher);
    const { data: clientsData } =
        useAuthorizedRequest<{ clients: Client[] }>('/clients', jsonFetcher);

    const clientMap = useMemo(() => {
        const map: Record<string, Client> = {};
        for (const c of clientsData?.clients ?? []) map[c.id] = c;
        return map;
    }, [clientsData]);

    const activeClients = useMemo(() => (clientsData?.clients ?? []).filter(c => !c.deleted), [clientsData]);

    // --- Client-side text filtering (on top of server-side filtered data) ---
    const payments = useMemo(() => {
        return (paymentsData?.payments ?? [])
            .filter(p => !p.deleted)
            .filter(p => {
                if (!searchText) return true;
                const client = clientMap[p.client_id];
                return [p.id, p.client_id, client?.name, String(p.amount), p.date_time, p.comment]
                    .some(s => s?.toLowerCase().includes(searchText.toLowerCase()));
            })
            .sort((a, b) => b.date_time.localeCompare(a.date_time));
    }, [paymentsData, clientMap, searchText]);

    async function handleCreatePayment(attrs: PaymentAttributes): Promise<string | undefined> {
        const token = await getAccessTokenSilently();
        const result = await apiRequest<{ payment: { id: string } }>('POST', '/payments', token, attrs);
        await mutatePayments();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
        return result?.payment?.id;
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')}: {(error as Error).message}</div>;

    const clientList = activeClients.sort((a, b) => a.name.localeCompare(b.name));

    return (
        <VStack gap={4} align="stretch">
            {/* Free-text search — always visible */}
            <HStack>
                <Input
                    placeholder={t('payments.search')}
                    variant="subtle"
                    flex="1"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                />
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setFilterPanelOpen(!filterPanelOpen)}
                >
                    {t('payments.filter_toggle')}
                    {activeFilterCount > 0 && (
                        <Badge ml={1} colorPalette="blue">{activeFilterCount}</Badge>
                    )}
                </Button>
                <Button size="sm" onClick={() => setCreateOpen(true)}>{t('payments.new')}</Button>
            </HStack>

            {/* Collapsible filter panel */}
            <Collapsible.Root open={filterPanelOpen} onOpenChange={e => setFilterPanelOpen(e.open)}>
                <Collapsible.Content>
                    <Box borderWidth="1px" borderRadius="md" p={4} bg="bg.subtle">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                            {/* Date range */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('payments.filter_date_from')}</Text>
                                <Input type="date" size="sm" value={getFilter('date_from')} onChange={e => setFilter('date_from', e.target.value)} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('payments.filter_date_to')}</Text>
                                <Input type="date" size="sm" value={getFilter('date_to')} onChange={e => setFilter('date_to', e.target.value)} />
                            </Box>

                            {/* Client picker */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('payments.filter_client')}</Text>
                                <select
                                    value={getFilter('client_id')}
                                    onChange={e => setFilter('client_id', e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">{t('payments.filter_client_placeholder')}</option>
                                    {clientList.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                                {clientList.length === 0 && (
                                    <Text fontSize="xs" color="gray.500">{t('payments.filter_no_clients')}</Text>
                                )}
                            </Box>

                            {/* Amount range */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('payments.filter_amount_min')}</Text>
                                <Input type="number" size="sm" min="0" placeholder="0"
                                    value={getFilter('amount_min')}
                                    onChange={e => setFilter('amount_min', e.target.value)} />
                            </Box>
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('payments.filter_amount_max')}</Text>
                                <Input type="number" size="sm" min="0" placeholder="999999"
                                    value={getFilter('amount_max')}
                                    onChange={e => setFilter('amount_max', e.target.value)} />
                            </Box>

                            {/* Comment text search */}
                            <Box>
                                <Text fontSize="sm" mb={1}>{t('payments.filter_comment')}</Text>
                                <Input type="text" size="sm" placeholder="..."
                                    value={getFilter('comment')}
                                    onChange={e => setFilter('comment', e.target.value)} />
                            </Box>
                        </Grid>

                        {/* Clear filters button */}
                        {activeFilterCount > 0 && (
                            <Button size="sm" variant="ghost" mt={4} onClick={clearFilters}>
                                {t('payments.filter_clear')}
                            </Button>
                        )}
                    </Box>
                </Collapsible.Content>
            </Collapsible.Root>

            {/* Payments table */}
            <Box overflowX="auto">
                <Table.Root variant="line" showColumnBorder interactive size="sm">
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>{t('label.date')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.client')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.amount')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isLoading ? (
                            [...Array(5)].map((_, i) => (
                                <Table.Row key={i}>
                                    {[...Array(4)].map((_, j) => (
                                        <Table.Cell key={j}><Skeleton height="20px" /></Table.Cell>
                                    ))}
                                </Table.Row>
                            ))
                        ) : payments.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={4} style={{ textAlign: 'center' }}>
                                    <Text color="gray.500">{t('label.no_data')}</Text>
                                </Table.Cell>
                            </Table.Row>
                        ) : payments.map(p => {
                            const client = clientMap[p.client_id];
                            return (
                                <Table.Row key={p.id}>
                                    <Table.Cell>{formatDate(p.date_time)}</Table.Cell>
                                    <Table.Cell>
                                        <Link to={`/client/${p.client_id}`} style={{ textDecoration: 'underline' }}>
                                            <Text>{client?.name ?? p.client_id}</Text>
                                            <Text fontSize="xs" color="gray.500">{p.client_id}</Text>
                                        </Link>
                                    </Table.Cell>
                                    <Table.Cell>{p.amount} ₴</Table.Cell>
                                    <Table.Cell>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Button size="xs" variant="ghost" onClick={() => navigate(`/payment/${p.id}`)} aria-label={t('action.edit')}>✏️</Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Positioner>
                                                <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                            </Tooltip.Positioner>
                                        </Tooltip.Root>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table.Root>
            </Box>
            {clientList.length > 0 && (
                <PaymentFormDrawer
                    title={t('payments.new').replace('+ ', '')}
                    clientOptions={clientList}
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    onSave={handleCreatePayment}
                    getAccessToken={getAccessTokenSilently}
                />
            )}
        </VStack>
    );
}
